<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="video-title"
          >
            <b-form-input
              id="video-title"
              v-model="form.title"
              :state="validateState('title')"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="video-slug"
          >
            <b-form-input
              id="video-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="video-lang"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.status')"
            label-for="video-status"
            :state="validateState('status')"
          >
            <b-form-select v-model="form.status">
              <b-form-select-option
                v-for="(status, index) in statusOptions"
                :key="index"
                :value="status"
              >
                {{ $t(`articles.${status}`) }}
              </b-form-select-option>
            </b-form-select>
            <b-form-invalid
              id="input-status-feedback"
              :label="$t('general.status')"
              :validator="$v.form.status"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="video-category"
            class="mb-2"
            :state="validateState('category')"
          >
            <v-select-custom
              id="video-category"
              v-model="form.category"
              :options="rootCategoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.tags')"
            label-for="video-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="video-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.approver')"
            label-for="video-approver"
          >
            <v-select
              id="article-approver"
              v-model="form.approver"
              dir="rtl"
              label="firstName"
              :options="approverOptions"
              @search="fetchApprovers(form.lang, $event)"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="video-editor"
          >
            <v-select-custom
              id="video-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.metaTitle')"
            label-for="article-category-metaTitle"
          >
            <b-form-input
              id="article-category-metaTitle"
              v-model="form.metaTitle"
              :state="validateState('metaTitle')"
            />
            <b-form-invalid
              id="input-metaTitle-feedback"
              :label="$t('general.metaTitle')"
              :validator="$v.form.metaTitle"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('articles.relatedArticles')"
            label-for="video-related-articles"
            :state="validateState('relatedArticles')"
          >
            <v-select
              id="video-related-articles"
              v-model="form.relatedArticles"
              dir="rtl"
              label="title"
              multiple
              :options="articleOptions"
              @search="fetchArticles(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
            <b-form-invalid
              id="input-relatedArticles-feedback"
              :label="$t('articles.relatedArticles')"
              :validator="$v.form.relatedArticles"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.embedVideo')"
            label-for="video-embedVideo"
          >
            <b-form-textarea
              id="video-embedVideo"
              v-model="form.embedVideo"
              :state="validateState('embedVideo')"
              rows="5"
            />
            <b-form-invalid
              id="input-embedVideo-feedback"
              :label="$t('general.embedVideo')"
              :validator="$v.form.embedVideo"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="video-keywords"
          >
            <b-form-input
              id="video-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.canonicalUrl')"
            label-for="video-canonicalUrl"
            class="mb-2"
          >
            <b-form-input
              id="video-canonicalUrl"
              v-model="form.canonicalUrl"
              :state="validateState('canonicalUrl')"
            />
            <b-form-invalid
              id="input-canonicalUrl-validator"
              :label="$t('general.canonicalUrl')"
              :validator="$v.form.canonicalUrl"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="video-description"
          >
            <b-form-textarea
              id="video-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <CampaignSettings
            :value="form.campaignSettings || {}"
            :lang="form.lang || 'fa'"
            end-only
            @input="$set(form, 'campaignSettings', $event)"
          />
          <ArticleDoctorEditor
            :mode="form.doctorBlockMode || 'auto'"
            :ids="form.recommendedDoctorIds || []"
            :enabled="form.doctorBlockEnabled === true"
            placement="end"
            :end-only="true"
            :tags="form.tags || []"
            :lang="form.lang || 'fa'"
            @change="setDoctorBlock"
          />
          <b-form-group label-for="video-content">
            <CkeditorBasic
              id="video-content"
              v-model="form.content"
              :label="$t('general.content')"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleThumbnail"
            v-model="form.articleThumbnail"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleThumbnail')"
            :alt.sync="form.articleThumbnailAlt"
            :alt-validator="$v.form.articleThumbnailAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-video-thumbnail-feedback"
                :label="$t('general.thumbnail')"
                :validator="$v.form.articleThumbnail"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleCoverImage"
            v-model="form.articleCoverImage"
            :label="$t('general.cover') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleCoverImage')"
            :alt.sync="form.articleCoverImageAlt"
            :alt-validator="$v.form.articleCoverImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-video-cover-feedback"
                :label="$t('general.cover')"
                :validator="$v.form.articleCoverImage"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleVideo"
            v-model="form.articleVideo"
            :label="$t('general.video') + ' ' + $t('general.acceptVideo')"
            :placeholder="$t('general.select')"
            :state="validateState('articleVideo')"
            :accept="'.mp4,.mov,.flv,.mkv'"
            :alt.sync="form.articleVideoAlt"
            :alt-validator="$v.form.articleVideoAlt"
            type="video"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-video-article-video-feedback"
                :label="$t('general.video')"
                :validator="$v.form.articleVideo"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import '@/components/BDataTable/Elements/ImageCell.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import vSelect from 'vue-select'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import useVideosList from './useVideosList'
import CampaignSettings from '../contentCampaign/CampaignSettings.vue'
import ArticleDoctorEditor from '../article/components/ArticleDoctorEditor.vue'

export default {
  name: 'VideoAdd',
  components: {
    ArticleDoctorEditor,
    CampaignSettings,
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        metaTitle: {
          required,
        },
        slug: {
          required,
          isUnique: () => this.isUniqueSlug,
        },
        tags: {
          required,
        },
        category: {
          required,
        },
        keywords: {
          required,
          minLength: minLength(3),
        },
        embedVideo: {
          required: requiredIf(function () {
            return !this.form.articleVideo
          }),
        },
        description: {
          required,
          minLength: minLength(10),
        },
        articleThumbnail: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.articleThumbnail === 'object' && !Object.prototype.hasOwnProperty.call(this.form.articleThumbnail, '_id')
          }),
          isValidImageSize,
        },
        articleThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
        articleCoverImage: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.articleCoverImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.articleCoverImage, '_id')
          }),
          isValidImageSize,
        },
        articleCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
        articleVideo: {
          required: requiredIf(function () {
            return !this.form.embedVideo
          }),
        },
        articleVideoAlt: {
          required: requiredIf(function () {
            return !this.form.embedVideo
          }),
        },
        relatedArticles: {},
        canonicalUrl: {},
        status: {
          required,
        },
      },
    }
  },
  setup() {
    const {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      rootCategoryOptions,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      itemLoading,
      newTagIds,
      statusOptions,
      tags,
      userOptions,
    } = useVideosList()
    return {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      rootCategoryOptions,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      itemLoading,
      newTagIds,
      statusOptions,
      tags,
      userOptions,
    }
  },
  data() {
    return {
      editorOptions: [],
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.articleVideoAlt) {
        this.$set(this.form, 'articleVideoAlt', newValue)
      }
      if (oldValue === this.form.articleCoverImageAlt) {
        this.$set(this.form, 'articleCoverImageAlt', newValue)
      }
      if (oldValue === this.form.articleThumbnailAlt) {
        this.$set(this.form, 'articleThumbnailAlt', newValue)
      }
    },
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.$set(this.form, 'lang', 'fa')
    this.fetchApprovers()
    this.fetchTags('fa')
    this.fetchUsersByRole({
      role: 'editor',
    }).then(res => {
      this.editorOptions = res
    })
    this.fetchCategories(this.form.lang, '', '', 'null')
    this.fetchArticles(this.form.lang)
  },
  methods: {
    setDoctorBlock(value) {
      Object.keys(value).forEach(key => this.$set(this.form, key, value[key]))
    },
    updateAltImage() {
      if (!this.form.articleVideoAlt) {
        this.$set(this.form, 'articleVideoAlt', this.form.title)
      }
      if (!this.form.articleCoverImageAlt) {
        this.$set(this.form, 'articleCoverImageAlt', this.form.title)
      }
      if (!this.form.articleThumbnailAlt) {
        this.$set(this.form, 'articleThumbnailAlt', this.form.title)
      }
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form.lang = selectedLanguage
      this.fetchCategories(selectedLanguage, '', '', 'null')
      this.fetchArticles(selectedLanguage)
      this.fetchTags(selectedLanguage)
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['articleCoverImage', 'articleThumbnail', 'articleVideo']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        metaTitle: form.metaTitle,
        slug: form.slug,
        keywords: form.keywords,
        description: form.description,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        approverId: form.approver ? form.approver._id : null,
        relatedArticleIds: form.relatedArticles ? form.relatedArticles.map(item => item._id) : [],
        articleCoverImageFileId: form.articleCoverImage ? form.articleCoverImage._id : null,
        articleThumbnailFileId: form.articleThumbnail ? form.articleThumbnail._id : null,
        articleVideoFileId: form.articleVideo ? form.articleVideo._id : null,
        writerId: null,
        editorId: form.editor ? form.editor._id : null,
        status: form.status,
        embedVideo: form.embedVideo,
        categoryId: form.category._id,
        content: form.content,
        ...(form.campaignSettings ? { campaignSettings: form.campaignSettings } : {}),
        recommendedDoctorIds: form.recommendedDoctorIds || [],
        doctorBlockMode: form.doctorBlockMode || 'auto',
        doctorBlockEnabled: form.doctorBlockMode === 'manual',
        doctorBlockPlacement: 'end',
        canonicalUrl: form.canonicalUrl,
        type: 'video',
      }
      this.addItem(data, this.form.lang || 'fa')
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss"
</style>
